import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { fillArray } from 'lib/arrayUtils'
import moment, { Moment } from 'moment'

type RoomRateId = string
type IsoDate = string

function populateTacticalScheduleDays(roomRateId: string, startDate: Moment, endDate: Moment, roomRateMap: Map<RoomRateId, Map<IsoDate, true>>) {
  const daysToFill = fillArray(endDate.diff(startDate, 'days') + 1)

  daysToFill.forEach((offset) => {
    if (!roomRateMap.get(roomRateId)) {
      roomRateMap.set(roomRateId, new Map())
    }

    const isoDate = startDate.clone().add(offset, 'days').format(ISO_DATE_FORMAT)
    roomRateMap.get(roomRateId)?.set(isoDate, true)
  })
}

// The idea here is, iterate through all the room rates, and then all the inclusions to determine when a room rate is tactical for a given calendar day...
// Create a map of room rate id to ANOTHER map that maps a given date to 'true', indicating whether or not the given date is within a tactical schedule range
// If an iso date has not been populated, we can assume that it does not have a tactical schedule, and we can return 'false'
// This makes it easy for the calendar components to lookup whether or not a given date is tactical for a given room rate
export default function roomRateTacticalScheduleMemoiser(packages: App.Package[]) {
  const roomRateTacticalScheduleMap = new Map<RoomRateId, Map<IsoDate, true>>()

  packages.forEach((pkg) => {
    if (!pkg.roomRate) return

    const roomRateId = pkg.roomRate.id
    let schedules = (pkg.roomRate.schedules ?? []).filter(s => s.type === 'tactical')
    schedules = schedules.concat((pkg.inclusions ?? []).reduce((acc, inclusion) => {
      return acc.concat((inclusion.schedules ?? []).filter(s => s.type === 'tactical'))
    }, [] as Array<App.ReservationSchedule>))
    schedules = schedules.concat((pkg.bonusInclusions ?? []).reduce((acc, inclusion) => {
      return acc.concat((inclusion.schedules ?? []).filter(s => s.type === 'tactical'))
    }, [] as Array<App.ReservationSchedule>))

    for (const schedule of schedules) {
      populateTacticalScheduleDays(
        roomRateId,
        moment(schedule.travelPeriod.from),
        moment(schedule.travelPeriod.to).subtract(pkg.duration, 'days'),
        roomRateTacticalScheduleMap,
      )
    }
  })

  return function(roomRateId: RoomRateId, isoDate: IsoDate) {
    return roomRateTacticalScheduleMap.get(roomRateId)?.get(isoDate) ?? false
  }
}
